import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Breadcrumbs,
  Link,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Star as StarIcon, Image as ImageIcon, ShowChart as ChartIcon } from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { productsService, tvasService, categoriesService, subcategoriesService, brandsService, familiesService } from '../services/api';
import { productVariantsService } from '../services/productVariantsService';
import { variantImagesService } from '../services/variantImagesService';
import LoadingSpinner from '../components/LoadingSpinner';
import ImageUpload from '../components/ImageUpload';
import CompetitorUrls from '../components/CompetitorUrls';

const ProductEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();



  // Déterminer si nous sommes en mode création
  const isNewProduct = location.pathname === '/products/new';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tvas, setTvas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [families, setFamilies] = useState([]);
  const [error, setError] = useState('');
  const [images, setImages] = useState([]);
  const [variants, setVariants] = useState([]);
  const [openVariantDialog, setOpenVariantDialog] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [variantFormData, setVariantFormData] = useState({
    name: '',
    priceHt: '',
    purchasePrice: '',
    tvaId: '',
    priceTtc: '',
    stock: '',
    stockMin: '',
    weight: '',
    isActive: true,
    competitorUrls: []
  });
  const [variantImages, setVariantImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    categoryId: '',
    subcategoryId: '',
    priceHt: '',
    priceTtc: '',
    purchasePrice: '',
    tvaId: '',
    brandId: '',
    familyIds: [],
    weight: '',
    stock: '',
    stockMin: '',
    stockMax: '',
    isNew: false,
    isBestSeller: false,
    isFeatured: false,
    isPromo: false,
    isActive: true,
    competitorUrls: []
  });

  useEffect(() => {
    fetchData();
  }, [id, isNewProduct]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Préparer les promesses pour les données
      const promises = [
        tvasService.getAll(),
        categoriesService.getAll(),
        subcategoriesService.getAll(),
        brandsService.getAll(),
        familiesService.getAll()
      ];

      // Ajouter la promesse pour le produit seulement si on modifie un produit existant
      if (!isNewProduct && id) {
        promises.unshift(productsService.getById(id));
      }

      const results = await Promise.all(promises);

      // Traiter les résultats
      let productData = null;
      let tvasData, categoriesData, subcategoriesData, brandsData, familiesData;

      if (!isNewProduct && id) {
        [productData, tvasData, categoriesData, subcategoriesData, brandsData, familiesData] = results;
      } else {
        [tvasData, categoriesData, subcategoriesData, brandsData, familiesData] = results;
      }

      // Mettre à jour le formulaire si on a des données de produit
      if (productData && productData.data) {
        // Déterminer la catégorie à partir de la sous-catégorie
        const subcategory = subcategoriesData.data.find(sub => sub.id == productData.data.subcategoryId);
        const categoryId = subcategory ? subcategory.categoryId : '';

        setFormData({
          name: productData.data.name || '',
          description: productData.data.description || '',
          categoryId: categoryId,
          subcategoryId: productData.data.subcategoryId || '',
          priceHt: productData.data.priceHt || '',
          priceTtc: productData.data.priceTtc || '',
          purchasePrice: productData.data.purchasePrice || '',
          tvaId: productData.data.tvaId || '',
          brandId: productData.data.brandId || '',
          familyIds: productData.data.families ? productData.data.families.map(f => f.id) : [],
          weight: productData.data.weight || '',
          stock: productData.data.stock || '',
          stockMin: productData.data.stockMin || '',
          stockMax: productData.data.stockMax || '',
          isNew: productData.data.isNew || false,
          isBestSeller: productData.data.isBestSeller || false,
          isFeatured: productData.data.isFeatured || false,
          isPromo: productData.data.isPromo || false,
          isActive: productData.data.isActive !== undefined ? productData.data.isActive : true,
          competitorUrls: Array.isArray(productData.data.competitorUrls) ? productData.data.competitorUrls : []
        });

        // Charger les images existantes si disponibles
        if (productData.data.images && productData.data.images.length > 0) {
          const existingImages = productData.data.images.map(img => ({
            id: img.id,
            preview: `${process.env.REACT_APP_URL || 'https://api.inklandtattoo.fr'}${img.url}`,
            name: img.alt || 'Image produit',
            file: null, // Pas de fichier pour les images existantes
            isExisting: true
          }));
          setImages(existingImages);
        }

        // Charger les variantes du produit
        if (productData.data.variants && productData.data.variants.length > 0) {
          console.log('Variantes chargées:', productData.data.variants);
          setVariants(productData.data.variants);
        }
      }

      setTvas(tvasData.data);
      setCategories(categoriesData.data);
      setSubcategories(subcategoriesData.data);
      setBrands(brandsData.data);
      setFamilies(familiesData.data);

      // Debug: vérifier que les données sont chargées
      console.log('Catégories chargées:', categoriesData.data);
      console.log('Sous-catégories chargées:', subcategoriesData.data);
      console.log('Marques chargées:', brandsData.data);
      console.log('Familles chargées:', familiesData.data);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');

      // Préparer les données du produit
      const productData = {
        name: formData.name,
        description: formData.description,
        subcategoryId: formData.subcategoryId,
        priceHt: parseFloat(formData.priceHt),
        priceTtc: formData.priceTtc ? parseFloat(formData.priceTtc) : null,
        purchasePrice: formData.purchasePrice ? parseFloat(formData.purchasePrice) : null,
        tvaId: parseInt(formData.tvaId),
        brandId: formData.brandId ? parseInt(formData.brandId) : null,
        familyIds: formData.familyIds,
        weight: parseFloat(formData.weight),
        stock: parseInt(formData.stock),
        stockMin: parseInt(formData.stockMin),
        stockMax: parseInt(formData.stockMax),
        isNew: formData.isNew,
        isBestSeller: formData.isBestSeller,
        isFeatured: formData.isFeatured,
        isPromo: formData.isPromo,
        isActive: formData.isActive,
        competitorUrls: Array.isArray(formData.competitorUrls) && formData.competitorUrls.length > 0 ? formData.competitorUrls : null,
      };

      console.log('Données du produit à envoyer:', productData);
      console.log('État du formulaire:', formData);

      let productId;
      if (isNewProduct) {
        const response = await productsService.create(productData);
        productId = response.data.id;
      } else {
        await productsService.update(id, productData);
        productId = id;
      }

      // Upload des nouvelles images seulement
      const newImages = images.filter(image => image.file && !image.isExisting);
      if (newImages.length > 0) {
        const formDataImages = new FormData();
        newImages.forEach((image, index) => {
          formDataImages.append('images', image.file);
        });

        await productsService.uploadImages(productId, formDataImages);
      }

      navigate('/products');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setError('Erreur lors de la sauvegarde du produit');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Fonctions de gestion des variantes
  const handleOpenVariantDialog = async (variant = null) => {
    if (variant) {
      setEditingVariant(variant);
      setVariantFormData({
        name: variant.name,
        priceHt: variant.priceHt,
        purchasePrice: variant.purchasePrice || '',
        tvaId: variant.tvaId,
        priceTtc: variant.priceTtc,
        stock: variant.stock,
        stockMin: variant.stockMin,
        weight: variant.weight,
        isActive: variant.isActive,
        competitorUrls: Array.isArray(variant.competitorUrls) ? variant.competitorUrls : []
      });

      // Recharger les images depuis l'API pour s'assurer d'avoir les dernières
      try {
        const variantWithImages = await productVariantsService.getById(variant.id);
        // Marquer les images existantes comme non-temporaires
        const existingImages = (variantWithImages.data.images || []).map(img => ({
          ...img,
          isTemp: false,
          isExisting: true
        }));
        setVariantImages(existingImages);
      } catch (error) {
        console.error('❌ Erreur lors du chargement des images pour l\'édition:', error);
        // Marquer les images existantes comme non-temporaires
        const existingImages = (variant.images || []).map(img => ({
          ...img,
          isTemp: false,
          isExisting: true
        }));
        setVariantImages(existingImages);
      }
    } else {
      setEditingVariant(null);
      setVariantFormData({
        name: '',
        priceHt: '',
        purchasePrice: '',
        tvaId: '',
        priceTtc: '',
        stock: '',
        stockMin: '',
        weight: '',
        isActive: true,
        competitorUrls: []
      });
      setVariantImages([]);
    }
    setOpenVariantDialog(true);
  };

  const handleCloseVariantDialog = () => {
    setOpenVariantDialog(false);
    setEditingVariant(null);
    setVariantFormData({
      name: '',
      priceHt: '',
      purchasePrice: '',
      stock: '',
      stockMin: '',
      weight: '',
      isActive: true,
      competitorUrls: []
    });
    setVariantImages([]);
  };

  const handleVariantSubmit = async () => {
    try {
      if (!id) {
        setError('Le produit doit être sauvegardé avant d\'ajouter des variantes');
        return;
      }

      // Validation des champs obligatoires
      if (!variantFormData.name || !variantFormData.priceHt || !variantFormData.tvaId) {
        setError('Veuillez remplir tous les champs obligatoires (Nom, Prix HT, TVA)');
        return;
      }

      const data = {
        ...variantFormData,
        productId: id,
        priceHt: parseFloat(variantFormData.priceHt),
        purchasePrice: variantFormData.purchasePrice ? parseFloat(variantFormData.purchasePrice) : null,
        stock: parseInt(variantFormData.stock),
        stockMin: parseInt(variantFormData.stockMin),
        weight: parseFloat(variantFormData.weight),
        competitorUrls: Array.isArray(variantFormData.competitorUrls) && variantFormData.competitorUrls.length > 0 ? variantFormData.competitorUrls : null
      };

      if (editingVariant) {
        // Mise à jour d'une variante existante
        const updatedVariant = await productVariantsService.update(editingVariant.id, data);

        // Mettre à jour la variante dans la liste locale
        setVariants(prevVariants =>
          prevVariants.map(variant =>
            variant.id === editingVariant.id
              ? { ...updatedVariant.data, tva: tvas.find(t => t.id == updatedVariant.data.tvaId) }
              : variant
          )
        );
      } else {
        // Création d'une nouvelle variante
        const newVariant = await productVariantsService.create(data);

        // Ajouter la nouvelle variante à la liste locale
        const variantWithTva = {
          ...newVariant.data,
          tva: tvas.find(t => t.id == newVariant.data.tvaId)
        };
        setVariants(prevVariants => [...prevVariants, variantWithTva]);

        // Upload des images temporaires si elles existent
        const tempImages = variantImages.filter(img => img.isTemp);
        if (tempImages.length > 0) {
          try {
            const formData = new FormData();
            tempImages.forEach(img => {
              formData.append('images', img.file);
            });

            await variantImagesService.uploadImages(newVariant.data.id, formData);

            // Mettre à jour la variante avec les images uploadées
            const variantsResponse = await productVariantsService.getByProduct(id);
            setVariants(variantsResponse.data);
          } catch (error) {
            console.error('Erreur lors de l\'upload des images:', error);
            // Ne pas bloquer la création de la variante si l'upload d'images échoue
          }
        }

        // Afficher un message de succès
        setError(''); // Effacer les erreurs précédentes
      }

      handleCloseVariantDialog();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la variante:', error);
      setError('Erreur lors de la sauvegarde de la variante');
    }
  };

  const handleEditVariant = (variant) => {
    handleOpenVariantDialog(variant);
  };

  const handleDeleteVariant = async (variantId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette variante ?')) {
      try {
        await productVariantsService.delete(variantId);

        // Supprimer la variante de la liste locale
        setVariants(prevVariants => prevVariants.filter(variant => variant.id !== variantId));
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        setError('Erreur lors de la suppression de la variante');
      }
    }
  };

  const handleSetDefaultVariant = async (variantId) => {
    try {
      await productVariantsService.setDefault(variantId);

      // Mettre à jour la liste locale : désactiver toutes les autres variantes par défaut
      setVariants(prevVariants =>
        prevVariants.map(variant => ({
          ...variant,
          isDefault: variant.id === variantId
        }))
      );
    } catch (error) {
      console.error('Erreur lors de la définition de la variante par défaut:', error);
      setError('Erreur lors de la définition de la variante par défaut');
    }
  };

  // Fonction pour calculer le prix TTC à partir du prix HT et de la TVA
  const calculateTtc = (priceHt, tvaId) => {
    if (!priceHt || !tvaId) return '';
    const tva = tvas.find(t => t.id == tvaId);
    if (!tva) return '';
    return (parseFloat(priceHt) * (1 + tva.taux / 100)).toFixed(2);
  };

  // Fonction pour calculer le prix HT à partir du prix TTC et de la TVA
  const calculateHt = (priceTtc, tvaId) => {
    if (!priceTtc || !tvaId) return '';
    const tva = tvas.find(t => t.id == tvaId);
    if (!tva) return '';
    return (parseFloat(priceTtc) / (1 + tva.taux / 100)).toFixed(2);
  };

  // Fonction pour vérifier si une TVA est sélectionnée
  const isTvaSelected = () => {
    return variantFormData.tvaId && variantFormData.tvaId !== '';
  };

  // Fonction pour obtenir les sous-catégories d'une catégorie
  const getSubcategoriesByCategory = (categoryId) => {
    if (!categoryId) return [];
    console.log('Recherche des sous-catégories pour la catégorie:', categoryId);
    console.log('Sous-catégories disponibles:', subcategories);
    const filtered = subcategories.filter(sub => sub.categoryId == categoryId);
    console.log('Sous-catégories filtrées:', filtered);
    return filtered;
  };

  // Fonction pour obtenir la catégorie d'une sous-catégorie
  const getCategoryBySubcategory = (subcategoryId) => {
    if (!subcategoryId) return null;
    const subcategory = subcategories.find(sub => sub.id == subcategoryId);
    if (!subcategory) return null;
    return categories.find(cat => cat.id == subcategory.categoryId);
  };



  // Fonction pour ouvrir le dialogue de gestion des images

  // Fonction pour uploader des images de variante
  const handleUploadVariantImages = async (formData) => {
    try {
      const response = await variantImagesService.uploadImages(selectedVariant.id, formData);

      // Mettre à jour la liste des images
      setVariantImages(response.data.images);

      // Mettre à jour la variante dans la liste principale
      setVariants(prevVariants =>
        prevVariants.map(variant =>
          variant.id === selectedVariant.id
            ? { ...variant, images: response.data.images }
            : variant
        )
      );
    } catch (error) {
      console.error('Erreur lors de l\'upload des images:', error);
      setError('Erreur lors de l\'upload des images');
    }
  };

  // Fonction pour supprimer une image de variante
  const handleDeleteVariantImage = async (imageId) => {
    try {
      console.log('Suppression de l\'image depuis le dialogue principal:', imageId);

      // Trouver l'image à supprimer
      const imageToDelete = variantImages.find(img => img.id === imageId);
      console.log('Image à supprimer:', imageToDelete);

      if (imageToDelete && !imageToDelete.isTemp) {
        console.log('Suppression de l\'image existante depuis la base de données');
        await variantImagesService.deleteImage(imageId);

        // Mettre à jour la liste des images
        setVariantImages(prevImages => {
          const filtered = prevImages.filter(img => img.id !== imageId);
          console.log('Images après suppression (dialogue principal):', filtered);
          return filtered;
        });

        // Mettre à jour la variante dans la liste principale
        setVariants(prevVariants =>
          prevVariants.map(variant =>
            variant.id === selectedVariant.id
              ? { ...variant, images: (variant.images || []).filter(img => img.id !== imageId) }
              : variant
          )
        );
      } else {
        console.log('Suppression de l\'image temporaire ou nouvelle');
        // Supprimer de la liste temporaire
        setVariantImages(prevImages => {
          const filtered = prevImages.filter(img => img.id !== imageId);
          console.log('Images après suppression (dialogue principal):', filtered);
          return filtered;
        });
      }

      setError(''); // Effacer les erreurs précédentes
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'image:', error);
      setError('Erreur lors de la suppression de l\'image, Elle n\'est pas sur le serveur');
    }
  };

  // Fonction pour définir une image comme primaire
  const handleSetPrimaryImage = async (imageId) => {
    try {
      console.log('Définition de l\'image primaire:', imageId);

      await variantImagesService.setPrimaryImage(imageId);

      // Mettre à jour la liste des images
      setVariantImages(prevImages =>
        prevImages.map(img => ({
          ...img,
          isPrimary: img.id === imageId
        }))
      );

      // Mettre à jour la variante dans la liste principale
      setVariants(prevVariants =>
        prevVariants.map(variant =>
          variant.id === selectedVariant.id
            ? {
              ...variant,
              images: (variant.images || []).map(img => ({
                ...img,
                isPrimary: img.id === imageId
              }))
            }
            : variant
        )
      );

      setError(''); // Effacer les erreurs précédentes
    } catch (error) {
      console.error('Erreur lors de la définition de l\'image primaire:', error);
      setError('Erreur lors de la définition de l\'image primaire');
    }
  };

  // Fonction pour définir une image comme primaire dans le modal de variante
  const handleSetPrimaryVariantImage = async (imageId) => {
    try {
      console.log('🔍 Définition de l\'image primaire pour la variante:', imageId);

      // Mettre à jour l'état local des images
      setVariantImages(prevImages =>
        prevImages.map(img => ({
          ...img,
          isPrimary: img.id === imageId
        }))
      );

      // Si c'est une variante existante, appeler l'API
      if (editingVariant) {
        await variantImagesService.setPrimaryImage(imageId);

        // Mettre à jour la variante dans la liste principale
        setVariants(prevVariants =>
          prevVariants.map(variant =>
            variant.id === editingVariant.id
              ? {
                ...variant,
                images: (variant.images || []).map(img => ({
                  ...img,
                  isPrimary: img.id === imageId
                }))
              }
              : variant
          )
        );
      }

      setError(''); // Effacer les erreurs précédentes
    } catch (error) {
      console.error('❌ Erreur lors de la définition de l\'image primaire:', error);
      setError('Erreur lors de la définition de l\'image primaire');
    }
  };

  // Fonction pour uploader des images depuis le modal de variante
  const handleUploadVariantImagesFromModal = async (newImages) => {
    try {
      setUploadingImages(true);

      if (editingVariant) {
        // Mise à jour d'une variante existante
        // Convertir les nouvelles images en FormData
        const formData = new FormData();
        newImages.forEach(image => {
          if (image.file) {
            formData.append('images', image.file);
          }
        });

        if (formData.getAll('images').length > 0) {
          const response = await variantImagesService.uploadImages(editingVariant.id, formData);

          // Vérifier s'il y a déjà des images existantes
          const hasExistingImages = variantImages.some(img => img.isExisting);

          // Ajouter les nouvelles images aux existantes
          setVariantImages(prevImages => {
            const existingImages = prevImages.filter(img => img.isExisting);
            const newUploadedImages = response.data.images.map((img, index) => ({
              ...img,
              isTemp: false,
              isExisting: true,
              // Si c'est la première image de la variante, la définir comme primaire
              isPrimary: !hasExistingImages && index === 0
            }));
            return [...existingImages, ...newUploadedImages];
          });

          // Mettre à jour la variante dans la liste principale
          setVariants(prevVariants =>
            prevVariants.map(variant =>
              variant.id === editingVariant.id
                ? {
                  ...variant,
                  images: [...(variant.images || []), ...response.data.images.map((img, index) => ({
                    ...img,
                    // Si c'est la première image de la variante, la définir comme primaire
                    isPrimary: !hasExistingImages && index === 0
                  }))]
                }
                : variant
            )
          );
        }
      } else {
        // Pour une nouvelle variante, stocker temporairement les images
        const tempImages = newImages.map((image, index) => ({
          id: `temp-${Date.now()}-${index}`,
          url: image.preview,
          alt: image.name || `Image ${index + 1}`,
          order: index,
          isPrimary: index === 0,
          file: image.file, // Garder la référence au fichier
          isTemp: true
        }));
        setVariantImages(tempImages);
      }
    } catch (error) {
      console.error('Erreur lors de l\'upload des images:', error);
      setError('Erreur lors de l\'upload des images');
    } finally {
      setUploadingImages(false);
    }
  };

  // Fonction pour supprimer une image depuis le modal
  const handleDeleteVariantImageFromModal = async (imageId) => {
    try {
      console.log('Suppression de l\'image:', imageId);
      console.log('Images actuelles:', variantImages);

      // Trouver l'image à supprimer
      const imageToDelete = variantImages.find(img => img.id === imageId);
      console.log('Image à supprimer:', imageToDelete);

      if (editingVariant && imageToDelete && !imageToDelete.isTemp) {
        console.log('Suppression de l\'image existante depuis la base de données');
        // Supprimer de la base de données
        await variantImagesService.deleteImage(imageId);

        // Mettre à jour la liste des images
        setVariantImages(prevImages => {
          const filtered = prevImages.filter(img => img.id !== imageId);
          console.log('Images après suppression:', filtered);
          return filtered;
        });

        // Mettre à jour la variante dans la liste principale
        setVariants(prevVariants =>
          prevVariants.map(variant =>
            variant.id === editingVariant.id
              ? { ...variant, images: (variant.images || []).filter(img => img.id !== imageId) }
              : variant
          )
        );
      } else {
        console.log('Suppression de l\'image temporaire ou nouvelle');
        // Supprimer de la liste temporaire
        setVariantImages(prevImages => {
          const filtered = prevImages.filter(img => img.id !== imageId);
          console.log('Images temporaires après suppression:', filtered);
          return filtered;
        });
      }

      setError(''); // Effacer les erreurs précédentes
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'image:', error);
      setError('Erreur lors de la suppression de l\'image, Elle n\'est pas sur le serveur');
    }
  };

  if (loading) {
    return <LoadingSpinner message="Chargement du produit..." />;
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          href="/products"
          onClick={(e) => {
            e.preventDefault();
            navigate('/products');
          }}
        >
          Produits
        </Link>
        <Typography color="text.primary">
          {isNewProduct ? 'Nouveau produit' : 'Modifier le produit'}
        </Typography>
      </Breadcrumbs>

      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        {isNewProduct ? 'Nouveau produit' : 'Modifier le produit'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom du produit"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>

            {/* Sélection de catégorie et sous-catégorie */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Catégorie"
                value={formData.categoryId || ''}
                onChange={(e) => {
                  const categoryId = e.target.value;
                  // Réinitialiser la sous-catégorie si on change de catégorie
                  setFormData({
                    ...formData,
                    categoryId: categoryId,
                    subcategoryId: ''
                  });
                }}
                required
              >
                <MenuItem value="">
                  <em>Sélectionner une catégorie</em>
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Sous-catégorie"
                value={formData.subcategoryId}
                onChange={(e) => setFormData({ ...formData, subcategoryId: e.target.value })}
                required
                disabled={!formData.categoryId}
                helperText={
                  formData.categoryId
                    ? `${getSubcategoriesByCategory(formData.categoryId).length} sous-catégorie(s) disponible(s)`
                    : 'Sélectionnez d\'abord une catégorie'
                }
              >
                <MenuItem value="">
                  <em>Sélectionner une sous-catégorie</em>
                </MenuItem>
                {formData.categoryId && getSubcategoriesByCategory(formData.categoryId).map((subcategory) => (
                  <MenuItem key={subcategory.id} value={subcategory.id}>
                    {subcategory.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {/* Section des prix - tous côte à côte */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Prix et TVA
              </Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Prix HT (Vente)"
                type="number"
                value={formData.priceHt}
                onChange={(e) => {
                  const newPriceHt = e.target.value;
                  const newPriceTtc = calculateTtc(newPriceHt, formData.tvaId);
                  setFormData({
                    ...formData,
                    priceHt: newPriceHt,
                    priceTtc: newPriceTtc
                  });
                }}
                required
                inputProps={{ min: 0, step: 0.01 }}
                helperText={
                  formData.tvaId
                    ? "Le prix TTC sera calculé automatiquement"
                    : 'Sélectionnez d\'abord une TVA'
                }
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Prix TTC"
                type="number"
                value={formData.priceTtc || calculateTtc(formData.priceHt, formData.tvaId)}
                onChange={(e) => {
                  const newPriceTtc = e.target.value;
                  const newPriceHt = calculateHt(newPriceTtc, formData.tvaId);
                  setFormData({
                    ...formData,
                    priceTtc: newPriceTtc,
                    priceHt: newPriceHt
                  });
                }}
                inputProps={{ min: 0, step: 0.01 }}
                helperText={
                  formData.tvaId
                    ? "Le prix HT sera calculé automatiquement"
                    : 'Sélectionnez d\'abord une TVA'
                }
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>TVA</InputLabel>
                <Select
                  value={formData.tvaId}
                  onChange={(e) => {
                    const newTvaId = e.target.value;
                    // Si on a un prix HT, recalculer le prix TTC
                    if (formData.priceHt) {
                      const newPriceTtc = calculateTtc(formData.priceHt, newTvaId);
                      setFormData({
                        ...formData,
                        tvaId: newTvaId,
                        priceTtc: newPriceTtc
                      });
                    }
                    // Si on a un prix TTC, recalculer le prix HT
                    else if (formData.priceTtc) {
                      const newPriceHt = calculateHt(formData.priceTtc, newTvaId);
                      setFormData({
                        ...formData,
                        tvaId: newTvaId,
                        priceHt: newPriceHt
                      });
                    }
                    // Sinon, juste changer la TVA
                    else {
                      setFormData({ ...formData, tvaId: newTvaId });
                    }
                  }}
                  required
                >
                  {tvas.map((tva) => (
                    <MenuItem key={tva.id} value={tva.id}>
                      {tva.taux}%
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Prix d'achat HT"
                type="number"
                value={formData.purchasePrice}
                onChange={(e) => setFormData({ ...formData, purchasePrice: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Coût d'achat pour calculer la marge"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Marque</InputLabel>
                <Select
                  value={formData.brandId}
                  onChange={(e) => setFormData({ ...formData, brandId: e.target.value })}
                >
                  <MenuItem value="">
                    <em>Aucune marque</em>
                  </MenuItem>
                  {brands.filter(brand => brand.isActive).map((brand) => (
                    <MenuItem key={brand.id} value={brand.id}>
                      {brand.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Familles de Produits</InputLabel>
                <Select
                  multiple
                  value={formData.familyIds}
                  onChange={(e) => setFormData({ ...formData, familyIds: e.target.value })}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((familyId) => {
                        const family = families.find(f => f.id === familyId);
                        return family ? (
                          <Chip
                            key={familyId}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {family.icon && <span>{family.icon}</span>}
                                <span>{family.name}</span>
                              </Box>
                            }
                            size="small"
                            sx={{
                              backgroundColor: family.color ? `${family.color}20` : 'primary.light',
                              color: family.color || 'primary.contrastText',
                            }}
                          />
                        ) : null;
                      })}
                    </Box>
                  )}
                >
                  {families.filter(family => family.isActive).map((family) => (
                    <MenuItem key={family.id} value={family.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        {family.icon && (
                          <Box sx={{ fontSize: '1.2rem' }}>{family.icon}</Box>
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2">{family.name}</Typography>
                          {family.description && (
                            <Typography variant="caption" color="text.secondary">
                              {family.description}
                            </Typography>
                          )}
                        </Box>
                        {family.color && (
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              backgroundColor: family.color,
                              borderRadius: '50%',
                              border: '1px solid #ddd',
                            }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Sélectionnez une ou plusieurs familles pour ce produit
                </Typography>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Stock"
                type="number"
                value={formData.stock}
                onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                required
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Stock minimum"
                type="number"
                value={formData.stockMin}
                onChange={(e) => setFormData({ ...formData, stockMin: e.target.value })}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Stock maximum"
                type="number"
                value={formData.stockMax}
                onChange={(e) => setFormData({ ...formData, stockMax: e.target.value })}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Poids (g)"
                type="number"
                value={formData.weight}
                onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                inputProps={{ min: 0 }}
              />
            </Grid>
            {/* Options du produit */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Options du produit
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isNew}
                        onChange={(e) => setFormData({ ...formData, isNew: e.target.checked })}
                      />
                    }
                    label="Nouveau produit"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isBestSeller}
                        onChange={(e) => setFormData({ ...formData, isBestSeller: e.target.checked })}
                      />
                    }
                    label="Meilleur vendeur"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isFeatured}
                        onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
                      />
                    }
                    label="Mis en avant"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isPromo}
                        onChange={(e) => setFormData({ ...formData, isPromo: e.target.checked })}
                      />
                    }
                    label="En promotion"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      />
                    }
                    label="Produit actif"
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <ImageUpload
                images={images}
                onImagesChange={setImages}
                maxImages={5}
              />

            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => handleChange('isActive', e.target.checked)}
                    />
                  }
                  label="Actif"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isNew}
                      onChange={(e) => handleChange('isNew', e.target.checked)}
                    />
                  }
                  label="Nouveau"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isBestSeller}
                      onChange={(e) => handleChange('isBestSeller', e.target.checked)}
                    />
                  }
                  label="Meilleur vendeur"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isFeatured}
                      onChange={(e) => handleChange('isFeatured', e.target.checked)}
                    />
                  }
                  label="En vedette"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isPromo}
                      onChange={(e) => handleChange('isPromo', e.target.checked)}
                    />
                  }
                  label="Promotion"
                />
              </Box>
            </Grid>

            {/* URLs des concurrents */}
            <Grid item xs={12}>
              <CompetitorUrls
                urls={formData.competitorUrls || []}
                onChange={(urls) => setFormData({ ...formData, competitorUrls: urls })}
                productId={!isNewProduct ? id : null}
                type="product"
              />
            </Grid>

            {/* Section des variantes */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 2 }}>
                <Typography variant="h6">
                  Variantes du produit
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenVariantDialog()}
                  disabled={isNewProduct}
                >
                  Ajouter une variante
                </Button>
              </Box>

              <Paper sx={{ p: 2, mb: 2 }}>
                {variants.length > 0 ? (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                      {variants.length} variante(s) configurée(s)
                    </Typography>
                    {variants.map((variant, index) => (
                      <Box key={variant.id || index} sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        position: 'relative'
                      }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={2}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight="bold">
                                {variant.name}
                              </Typography>
                              {variant.isDefault && (
                                <Chip
                                  label="Défaut"
                                  color="primary"
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Grid>

                          <Grid item xs={2}>
                            <Typography variant="body2">
                              Prix HT: {variant.priceHt}€
                            </Typography>
                          </Grid>
                          <Grid item xs={2}>
                            <Typography variant="body2">
                              TVA: {variant.tva ? `${variant.tva.taux}%` : 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={2}>
                            <Typography variant="body2">
                              Prix TTC: {variant.priceTtc}€
                            </Typography>
                          </Grid>
                          <Grid item xs={2}>
                            <Typography variant="body2">
                              Stock: {variant.stock}
                            </Typography>
                          </Grid>
                          <Grid item xs={2}>
                            <Typography variant="body2">
                              Poids: {variant.weight}g
                            </Typography>
                          </Grid>
                          <Grid item xs={2}>
                            <Chip
                              label={variant.isActive ? 'Active' : 'Inactive'}
                              color={variant.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </Grid>
                        </Grid>

                        {/* Affichage des images de la variante */}
                        {variant.images && variant.images.length > 0 && (
                          <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                            <Typography variant="body2" fontWeight="bold" gutterBottom>
                              Images ({variant.images.length})
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {variant.images.map((image, index) => (
                                <Box key={image.id} sx={{ position: 'relative' }}>
                                  <img
                                    src={`${process.env.REACT_APP_API_URL || 'https://api.inklandtattoo.fr'}${image.url}`}
                                    alt={image.alt || `Image ${index + 1}`}
                                    style={{
                                      width: 60,
                                      height: 60,
                                      objectFit: 'cover',
                                      borderRadius: 4,
                                      border: image.isPrimary ? '2px solid #1976d2' : '1px solid #ddd'
                                    }}
                                  />

                                  {/* Bouton pour définir comme primaire */}
                                  <IconButton
                                    size="small"
                                    onClick={async () => {
                                      try {

                                        // Appeler l'API pour définir l'image comme primaire
                                        await variantImagesService.setPrimaryImage(image.id);

                                        // Mettre à jour la variante dans la liste
                                        setVariants(prevVariants =>
                                          prevVariants.map(v =>
                                            v.id === variant.id
                                              ? {
                                                ...v,
                                                images: v.images.map(img => ({
                                                  ...img,
                                                  isPrimary: img.id === image.id
                                                }))
                                              }
                                              : v
                                          )
                                        );

                                        console.log('✅ Image définie comme primaire avec succès');
                                      } catch (error) {
                                        console.error('❌ Erreur lors de la définition de l\'image primaire:', error);
                                        setError('Erreur lors de la définition de l\'image primaire');
                                      }
                                    }}
                                    sx={{
                                      position: 'absolute',
                                      bottom: -8,
                                      left: -8,
                                      backgroundColor: image.isPrimary ? 'primary.main' : 'rgba(0,0,0,0.7)',
                                      color: 'white',
                                      width: 20,
                                      height: 20,
                                      '&:hover': {
                                        backgroundColor: image.isPrimary ? 'primary.dark' : 'rgba(0,0,0,0.9)'
                                      }
                                    }}
                                    title={image.isPrimary ? "Image primaire" : "Définir comme primaire"}
                                  >
                                    <StarIcon sx={{ fontSize: 12 }} />
                                  </IconButton>

                                  <Chip
                                    label={image.isPrimary ? "" : ""}
                                    size="medium"
                                    color={image.isPrimary ? "primary" : "warning"}
                                    sx={{
                                      position: 'absolute',
                                      top: -8,
                                      right: -8,
                                      fontSize: '0.6rem',
                                      height: '24px'
                                    }}
                                  />
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* Boutons d'action pour chaque variante */}
                        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>

                          {!variant.isDefault && (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleSetDefaultVariant(variant.id)}
                              sx={{ mr: 1 }}
                              title="Définir comme défaut"
                            >
                              <StarIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => handleEditVariant(variant)}
                            sx={{ mr: 1 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteVariant(variant.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Aucune variante configurée. Ajoutez des variantes pour ce produit.
                  </Typography>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                <Box>
                  {!isNewProduct && formData.competitorUrls && formData.competitorUrls.length > 0 && (
                    <Button
                      variant="outlined"
                      startIcon={<ChartIcon />}
                      onClick={() => navigate('/price-comparison')}
                      color="info"
                    >
                      Voir les prix concurrents
                    </Button>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/products')}
                    disabled={saving}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving}
                  >
                    {saving ? 'Sauvegarde...' : (isNewProduct ? 'Créer' : 'Modifier')}
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Dialogue pour créer/éditer une variante */}
      <Dialog open={openVariantDialog} onClose={handleCloseVariantDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingVariant ? 'Modifier la Variante' : 'Nouvelle Variante'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Nom de la variante"
                value={variantFormData.name}
                onChange={(e) => setVariantFormData({ ...variantFormData, name: e.target.value })}
                required
              />
            </Grid>



            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Prix HT Vente (€)"
                type="number"
                value={variantFormData.priceHt}
                onChange={(e) => {
                  const newPriceHt = e.target.value;
                  const newPriceTtc = calculateTtc(newPriceHt, variantFormData.tvaId);
                  setVariantFormData({
                    ...variantFormData,
                    priceHt: newPriceHt,
                    priceTtc: newPriceTtc
                  });
                }}
                required
                inputProps={{ min: 0, step: 0.01 }}
                helperText={isTvaSelected() ? "Le prix TTC sera calculé automatiquement" : "Sélectionnez d'abord une TVA"}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Prix d'achat HT (€)"
                type="number"
                value={variantFormData.purchasePrice}
                onChange={(e) => setVariantFormData({ ...variantFormData, purchasePrice: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Coût d'achat pour calculer la marge"
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="TVA"
                value={variantFormData.tvaId}
                onChange={(e) => {
                  const newTvaId = e.target.value;
                  // Recalculer le prix TTC si on a un prix HT
                  if (variantFormData.priceHt) {
                    const newPriceTtc = calculateTtc(variantFormData.priceHt, newTvaId);
                    setVariantFormData({
                      ...variantFormData,
                      tvaId: newTvaId,
                      priceTtc: newPriceTtc
                    });
                  }
                  // Recalculer le prix HT si on a un prix TTC
                  else if (variantFormData.priceTtc) {
                    const newPriceHt = calculateHt(variantFormData.priceTtc, newTvaId);
                    setVariantFormData({
                      ...variantFormData,
                      tvaId: newTvaId,
                      priceHt: newPriceHt
                    });
                  }
                  // Sinon, juste changer la TVA
                  else {
                    setVariantFormData({ ...variantFormData, tvaId: newTvaId });
                  }
                }}
                required
              >
                {tvas.map((tva) => (
                  <MenuItem key={tva.id} value={tva.id}>
                    {tva.name || `TVA ${tva.taux}%`} ({tva.taux}%)
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Prix TTC (€)"
                type="number"
                value={variantFormData.priceTtc}
                onChange={(e) => {
                  const newPriceTtc = e.target.value;
                  const newPriceHt = calculateHt(newPriceTtc, variantFormData.tvaId);
                  setVariantFormData({
                    ...variantFormData,
                    priceTtc: newPriceTtc,
                    priceHt: newPriceHt
                  });
                }}
                required
                inputProps={{ min: 0, step: 0.01 }}
                helperText={isTvaSelected() ? "Le prix HT sera calculé automatiquement" : "Sélectionnez d'abord une TVA"}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Stock"
                type="number"
                value={variantFormData.stock}
                onChange={(e) => setVariantFormData({ ...variantFormData, stock: e.target.value })}
                required
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Stock minimum"
                type="number"
                value={variantFormData.stockMin}
                onChange={(e) => setVariantFormData({ ...variantFormData, stockMin: e.target.value })}
                required
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Poids (g)"
                type="number"
                value={variantFormData.weight}
                onChange={(e) => setVariantFormData({ ...variantFormData, weight: e.target.value })}
                required
                inputProps={{ min: 0 }}
              />
            </Grid>



            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={variantFormData.isActive}
                    onChange={(e) => setVariantFormData({ ...variantFormData, isActive: e.target.checked })}
                  />
                }
                label="Variante active"
              />
            </Grid>

            {/* URLs des concurrents pour la variante */}
            <Grid item xs={12}>
              <CompetitorUrls
                urls={variantFormData.competitorUrls || []}
                onChange={(urls) => setVariantFormData({ ...variantFormData, competitorUrls: urls })}
                variantId={editingVariant ? editingVariant.id : null}
                type="variant"
              />
            </Grid>

            {/* Section des images */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Images de la variante
              </Typography>

              {/* Upload d'images */}
              <ImageUpload
                onImagesChange={handleUploadVariantImagesFromModal}
                onSetPrimaryImage={handleSetPrimaryVariantImage}
                images={variantImages.map(img => ({
                  ...img,
                  // S'assurer que les images ont les bonnes propriétés
                  name: img.alt || img.name || `Image ${img.id}`,
                  isExisting: !img.isTemp && !img.file
                }))}
                maxImages={10}
                title="Images de la variante"
              />

              {uploadingImages && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Upload des images en cours...
                  </Typography>
                </Box>
              )}

              {/* Affichage des images existantes */}
              {variantImages.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Images ({variantImages.length})
                  </Typography>
                  <Grid container spacing={1}>
                    {variantImages.map((image, index) => (
                      <Grid item xs={3} key={image.id || `temp-${index}`}>
                        <Box sx={{ position: 'relative' }}>
                          <img
                            src={image.isTemp ? image.url : `${process.env.REACT_APP_API_URL || 'https://api.inklandtattoo.fr'}${image.url}`}
                            alt={image.alt || `Image ${index + 1}`}
                            style={{
                              width: '100%',
                              height: 80,
                              objectFit: 'cover',
                              borderRadius: 4,
                              border: image.isPrimary ? '2px solid #1976d2' : '1px solid #ddd'
                            }}
                          />

                          {/* Indicateur d'image primaire */}
                          {image.isPrimary && (
                            <Chip
                              label="Primaire"
                              size="small"
                              color="primary"
                              sx={{
                                position: 'absolute',
                                top: 4,
                                left: 4,
                                fontSize: '0.6rem'
                              }}
                            />
                          )}

                          {/* Bouton étoile pour définir comme primaire - en bas à gauche */}
                          <IconButton
                            size="small"
                            onClick={async () => {
                              try {
                                console.log('Définition de l\'image primaire depuis le modal:', image.id);

                                if (editingVariant) {
                                  // Appeler l'API pour définir l'image primaire
                                  await variantImagesService.setPrimaryImage(image.id);

                                  // Mettre à jour la liste des images
                                  setVariantImages(prevImages =>
                                    prevImages.map(img => ({
                                      ...img,
                                      isPrimary: img.id === image.id
                                    }))
                                  );

                                  // Mettre à jour la variante dans la liste principale
                                  setVariants(prevVariants =>
                                    prevVariants.map(variant =>
                                      variant.id === editingVariant.id
                                        ? {
                                          ...variant,
                                          images: (variant.images || []).map(img => ({
                                            ...img,
                                            isPrimary: img.id === image.id
                                          }))
                                        }
                                        : variant
                                    )
                                  );
                                } else {
                                  // Pour les nouvelles variantes, mettre à jour localement
                                  setVariantImages(prevImages =>
                                    prevImages.map(img => ({
                                      ...img,
                                      isPrimary: img.id === image.id
                                    }))
                                  );
                                }

                                setError(''); // Effacer les erreurs précédentes
                              } catch (error) {
                                console.error('Erreur lors de la définition de l\'image primaire:', error);
                                setError('Erreur lors de la définition de l\'image primaire');
                              }
                            }}
                            sx={{
                              position: 'absolute',
                              bottom: 4,
                              left: 4,
                              backgroundColor: image.isPrimary ? 'primary.main' : 'rgba(0,0,0,0.7)',
                              color: 'white',
                              width: 20,
                              height: 20,
                              '&:hover': {
                                backgroundColor: image.isPrimary ? 'primary.dark' : 'rgba(0,0,0,0.9)'
                              }
                            }}
                            title={image.isPrimary ? "Image primaire" : "Définir comme primaire"}
                          >
                            <StarIcon sx={{ fontSize: 12 }} />
                          </IconButton>

                          {/* Boutons d'action - en bas à droite */}
                          <Box sx={{
                            position: 'absolute',
                            bottom: 4,
                            right: 4,
                            display: 'flex',
                            gap: 0.5
                          }}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={async () => {


                                try {


                                  if (editingVariant && !image.isTemp) {
                                    console.log('Suppression de l\'image existante depuis la base de données');
                                    // Supprimer de la base de données
                                    await variantImagesService.deleteImage(image.id);

                                    // Mettre à jour la liste des images
                                    setVariantImages(prevImages => {
                                      const filtered = prevImages.filter(img => img.id !== image.id);
                                      console.log('Images après suppression depuis le modal:', filtered);
                                      return filtered;
                                    });

                                    // Mettre à jour la variante dans la liste principale
                                    setVariants(prevVariants =>
                                      prevVariants.map(variant =>
                                        variant.id === editingVariant.id
                                          ? { ...variant, images: (variant.images || []).filter(img => img.id !== image.id) }
                                          : variant
                                      )
                                    );
                                  } else {
                                    console.log('Suppression de l\'image temporaire ou nouvelle');
                                    // Supprimer de la liste temporaire
                                    setVariantImages(prevImages => {
                                      const filtered = prevImages.filter(img => img.id !== image.id);
                                      console.log('Images temporaires après suppression depuis le modal:', filtered);
                                      return filtered;
                                    });
                                  }

                                  setError(''); // Effacer les erreurs précédentes
                                } catch (error) {
                                  console.error('Erreur lors de la suppression de l\'image depuis le modal:', error);
                                  setError('Erreur lors de la suppression de l\'image, Elle n\'est pas sur le serveur');
                                }
                              }}
                              sx={{
                                backgroundColor: 'rgba(255,255,255,0.9)',
                                '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                                width: 20,
                                height: 20
                              }}
                              title="Supprimer l'image"
                            >
                              <DeleteIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVariantDialog}>Annuler</Button>
          <Button onClick={handleVariantSubmit} variant="contained">
            {editingVariant ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue de gestion des images de variante */}

    </Box>
  );
};

export default ProductEdit;
